import { LocationUpgradeModule } from "@angular/common/upgrade";
import { Injectable, NgModule } from "@angular/core";
import {
  Route,
  RouterModule,
  Routes,
  UrlHandlingStrategy,
} from "@angular/router";
import { AdaptiveRulesAdminComponent } from "./adaptive-rules/adaptive-rules-admin.component";
import { AdaptiveRulesComponent } from "./adaptive-rules/adaptive-rules.component";
import { AuthenticationGuard } from "./auth/authentication.guard";
import { AuthenticationLoginRedirectGuard } from "./auth/authtication-login-redirect.guard";
import { LoginComponent } from "./auth/login.component";
import { AngularJSComponent } from "./hybrid/angular-js.component";
import { AdminSystemComponent } from "./modules/admin/admin-system/admin-system.component";
import { Ng1PlaceholderComponent } from "./ng1-placeholder.component";
import { SupportComponent } from "./support/support.component";
import { TemplateSettingsComponent } from "./template-settings/template-settings.component";
import { CommunicationsComponent } from './communications/communications.component';
/**
 * This table is used by the TopNavigationBarComponent to display items in the top navigation bar and (soon)
 * by the AdminSideBarComponent (not created yet). It's also used by the RouterModule (and sub routing modules).
 * The 'data' parameter includes custom parameters:
 *   ng1: true if the main view is rendered by angularJS (and component is set to Ng1PlaceholderComponent)
 *   parent: 'top' means the entry will be included in the top navigation bar
 *   label: the label to click on change to the route
 *   permissions: an array of permissions that the user must have for the entry to be visible (and route allowed)
 */
export const APP_ROUTES: Routes = [
  {
    path: "index.html", // required when running at nts2 from local
    redirectTo: "dashboard",
    canActivate: [AuthenticationGuard],
    pathMatch: "full",
  },
  {
    path: "",
    redirectTo: "dashboard",
    canActivate: [AuthenticationGuard],
    pathMatch: "full",
  },
  {
    path: "login",
    component: LoginComponent,
    canActivate: [AuthenticationLoginRedirectGuard],
  },
  {
    path: "dashboard",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    // redirectTo: '', // no component while handled by angularJS
    pathMatch: "full",
    data: {
      label: "Dashboard",
      icon: "glyphicon glyphicon-home",
      parent: "top",
      ng1: true,
    },
  },
  {
    path: "patients",
    redirectTo: "patients/",
    pathMatch: "full",
    data: {
        permissions: [
            { key: "autoremind3" },
            { deniedIfHasKey: "resource:pets" },
        ],
    }
  },
  {
    path: "patients",
    loadChildren: () =>
      import("./reminders/patients/patients.module").then(
        (m) => m.PatientsModule
      ),
    canActivate: [AuthenticationGuard],
    data: {
      label: "Patients",
      icon: "glyphicon glyphicon-user",
      parent: "top",
      permissions: [
        { key: "autoremind3" },
        { deniedIfHasKey: "resource:pets" },
      ],
      ng1: false,
    },
  },
  {
    path: "clients",
    redirectTo: "clients/",
    pathMatch: "full",
      data: {
          permissions: [{ key: "resource:pets" }]
      }
  },
  {
    path: "clients",
    loadChildren: () =>
      import("./reminders/patients/patients.module").then(
        (m) => m.PatientsModule
      ),
    canActivate: [AuthenticationGuard],
    data: {
      label: "Clients",
      icon: "glyphicon glyphicon-user",
      parent: "top",
      permissions: [{ key: "resource:pets" }],
      ng1: false,
    },
  },
  {
    path: "appointments",
    loadChildren: () =>
      import("./reminders/appointments/appointments.module").then(
        (m) => m.AppointmentsModule
      ),
    canActivate: [AuthenticationGuard],
    data: {
      label: "Appointments",
      icon: "glyphicon glyphicon-bell",
      parent: "top",
      permissions: [{ key: "autoremind3" }],
      ng1: false, // not used
    },
  },
  {
    path: "reminders",
    loadChildren: () =>
      import("./reminders/reminder-settings/reminder-settings.module").then(
        (m) => m.ReminderSettingsModule
      ),
    canActivate: [AuthenticationGuard],
    data: {
      label: "Reminders",
      icon: "glyphicon glyphicon-hand-right",
      parent: "top",
      permissions: [{ key: "autoremind3" }],
      ng1: false,
      reuseRoute:false
    },
  },
  {
    path: "communications",
    component: AngularJSComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Communications",
      icon: "glyphicon glyphicon-briefcase",
      parent: "top",
      permissions: [{ key: "communication" }],
      ng1: true
    },
  },
  {
    path: "communicationsnew",
    component: CommunicationsComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Communications",
      icon: "glyphicon glyphicon-briefcase",
      parent: "no",
      permissions: [{ key: "communication" }],
      ng1: false
    },
  },
  {
    path: "reports",
    redirectTo: "reports/",
    pathMatch: "full",
      data: {
          permissions: [{ key: "communication" }, { key: "autoremind3" }],
      }
  },
  {
    path: "reports/:type?",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Reports",
      icon: "glyphicon glyphicon-file",
      parent: "top",
      permissions: [{ key: "communication" }, { key: "autoremind3" }],
      ng1: true,
    },
  },
  {
    path: "two-way",
    loadChildren: () =>
      import("./modules/two-way/two-way.module").then((m) => m.TwoWayModule),
    canActivate: [AuthenticationGuard],
    data: {
      label: "Two-Way",
      icon: "glyphicon glyphicon-retweet",
      parent: "top",
      permissions: [{ key: "twoway" }],
      ng1: false,
    },
  },
  {
    path: "csv",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Practice Choice",
      icon: "glyphicon glyphicon-upload",
      parent: "top",
      permissions: [
        { key: "csv:practicechoice" },
        { page: "csvpracticechoice" },
        { username: "PrimaryCareAssociates" },
        { username: "AdvancedFamilyCare" },
        { username: "progressivewomenshealth" },
        { username: "ProgressiveBehavioralHealth" },
      ],
      ng1: true,
    },
  },
  {
    path: "csvuspi",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "USPI Import",
      icon: "glyphicon glyphicon-upload",
      parent: "top",
      permissions: [
        { key: "csv:uspi" },
        { page: "csvuspi" },
        { username: "desertridge" },
        { type: "portal", address: "csv_uspi" },
      ],
      ng1: true,
    },
  },
  {
    path: "csv_webselfstorage",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "StorageReminder",
      icon: "glyphicon glyphicon-upload",
      parent: "top",
      permissions: [
        { key: "csv:webselfstorage" },
        { page: "csvwebselfstorage" },
        { username: "webselfstorage" },
        { username: "WestChesterMasonStoreandLock" },
      ],
      ng1: true,
    },
  },
  {
    path: "csvederm",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "eDerm Import",
      icon: "glyphicon glyphicon-upload",
      parent: "top",
      permissions: [
        { key: "csv:ederm" },
        { page: "csvederm" },
        { username: "ConnecticutRiverValley" },
      ],
      ng1: true,
    },
  },
  {
    path: "csvlatepayment",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Late Payment",
      icon: "glyphicon glyphicon-upload",
      parent: "top",
      permissions: [
        { key: "csv:latepayment" },
        { deniedIfHasKey: "communication" },
      ],
      ng1: true,
    },
  },
  {
    path: "logs",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Logs",
      icon: "glyphicon glyphicon-hdd",
      parent: "top",
      permissions: [{ key: "autoremind2" }],
      ng1: true,
    },
  },

  // Routes for the user profile menu on the right

  {
    path: "profile",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "My Profile",
      icon: "glyphicon glyphicon-user",
      parent: "profile",
      ng1: true,
    },
  },
  {
    path: "support",
    component: SupportComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Support",
      icon: "glyphicon glyphicon-question-sign",
      parent: "profile",
      ng1: false,
    },
  },
  {
    path: "inspire",
    loadChildren: () =>
      import("./inspire/inspire.module").then((m) => m.InspireModule),
    canActivate: [AuthenticationGuard],
    data: {
      label: "Inspire",
      icon: "inspire-icon",
      parent: "profile",
      ng1: false,
    },
  },

  // TODO: prevent click from load route
  {
    path: "dashboard",
    component: AdaptiveRulesComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Rules",
      icon: "glyphicon glyphicon-cog",
      parent: "profile",
      ng1: false,
      onClick: "displayAdaptiveRulesDialog",
      permissions: [{ key: "none" }],
      // [!] Could use this technique to place callback code right here
      // but the problem is: how to get BsModalService instance here
      // clickArgs: {callback: (args: any): void => { console.log(`_FOO ` + args.bar); }, args: {bar: 'WAR'}}
    },
  },
  // Routes for the user profile menu on the right

  {
    /* TODO: Find a way to avoid this path to work while still rendering the header in the sidebar */
    path: "users",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Users",
      icon: "glyphicon glyphicon-user",
      permissions: [{ key: "access" }],
      parent: "sidebar",
      ng1: true,
      id: "users",
      sidebar: "group",
    },
  },

  {
    path: "admin/users/find",
    redirectTo: "admin/users/find/",
    pathMatch: "full",
      data: {
          permissions: [{ key: "access" }]
      }
  },
  {
    path: "admin/users/find/:tabname",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Find",
      icon: "glyphicon glyphicon-user",
      permissions: [{ key: "access" }],
      ng1: true,
      parent: "users",
      sidebar: "/admin/users",
    },
  },
  {
    path: "admin/users/new",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "New",
      icon: "glyphicon glyphicon-user",
      permissions: [{ key: "access" }],
      ng1: true,
      parent: "users",
      sidebar: "/admin/users",
    },
  },
  {
    path: "admin/permissions",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Permissions",
      icon: "glyphicon glyphicon-cog",
      permissions: [{ key: "admin:user:permissiongroups" }],
      ng1: true,
      parent: "users",
      sidebar: "/admin/users",
    },
  },
  {
    path: "admin/templates",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Templates",
      icon: "glyphicon glyphicon-asterisk",
      permissions: [{ key: "admin" }],
      id: "templates",
      ng1: true,
      // parent: 'users',
      sidebar: "group",
    },
  },
  {
    path: "admin/templates/autoremind3",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Autoremind 3",
      icon: "glyphicon glyphicon-asterisk",
      permissions: [{ key: "admin" }],
      id: "autoremind3",
      ng1: true,
      parent: "templates",
      sidebar: "/admin/templates/autoremind3",
    },
  },
  {
    path: "admin/templates/adaptiverules",
    component: AdaptiveRulesAdminComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Adaptive Rules",
      icon: "glyphicon glyphicon-cog",
      parent: "templates",
      ng1: false,
      id: "adaptiveRule",
      permissions: [{ key: "admin" }],
      sidebar: "/admin/templates/adaptiverules",
    },
  },
  {
    path: "admin/templates/settings",
    component: TemplateSettingsComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Settings",
      icon: "glyphicon glyphicon-cog",
      ng1: false,
      parent: "templates",
      id: "templateSettings",
      permissions: [{ key: "admin" }],
      sidebar: "/admin/templates/settings",
    },
  },
  {
    path: "admin/misc",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Misc",
      icon: "glyphicon glyphicon-cog",
      permissions: [{ key: "admin" }],
      id: "misc",
      ng1: true,
      // parent: 'users',
      sidebar: "group",
    },
  },
  {
    path: "admin/messaging",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Messaging",
      icon: "glyphicon glyphicon-cog",
      permissions: [
        { key: "admin" },
        { username: "botest" },
        { username: "systemtest" },
      ],
      id: "messaging",
      ng1: true,
      parent: "misc",
      sidebar: "/admin/misc",
    },
  },
  {
    path: "admin/chargify",
    redirectTo: "admin/chargify/",
    data: {
      permissions: [{ key: "access" }]
    }
  },
  {
    path: "admin/chargify/:tabname",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Chargify",
      icon: "glyphicon glyphicon-cog",
      permissions: [
        { key: "admin" },
        { username: "botest" },
        { username: "systemtest" },
      ],
      id: "chargify",
      ng1: true,
      parent: "misc",
      sidebar: "/admin/misc",
    },
  },
  {
    path: "admin/satellites",
    redirectTo: "admin/satellites/",
    pathMatch: "full",
    data: {
          permissions: [{ key: "access" }]
    }
  },
  {
    path: "admin/satellites/:tabname",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Satellites",
      icon: "glyphicon glyphicon-cog",
      permissions: [{ key: "admin" }],
      ng1: true,
      parent: "misc",
      sidebar: "/admin/misc",
    },
  },
  {
    path: "admin/satellites/lasterrors",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Satellites Last Error",
      permissions: [{ key: "admin" }],
      ng1: true,
      parent: "/admin/misc",
      sidebar: "/admin/misc",
    },
  },

  {
    path: "admin/system",
    // TODO: Enable lazy loading when action to get system settings is moved out of admin module
    // loadChildren: () => import('./modules/admin/admin-system/admin-system.module')
    //    .then(m => m.AdminSystemModule),
    component: AdminSystemComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "System",
      icon: "glyphicon",
      permissions: [{ key: "admin" }],
      ng1: false,
      parent: "misc",
      sidebar: "/admin/misc",
    },
  },
  {
    path: "admin/queries",
    component: Ng1PlaceholderComponent,
    canActivate: [AuthenticationGuard],
    data: {
      label: "Queries",
      permissions: [{ key: "admin" }],
      ng1: true,
      parent: "misc",
      sidebar: "/admin/misc",
    },
  }
];

// TODO: Consider if we should split the routing table into multiple tables and how would that affect how we filter them for the UI navbar and sidebar

const routesForAngularJs: Array<Route> = APP_ROUTES.filter(
  (r) => r.data !== undefined && r.data.ng1 !== undefined && r.data.ng1 === true
);

// https://medium.com/@gsari/upgrade-from-angularjs-to-angular-2-15f3179b7849
@Injectable()
export class HybridUrlHandlingStrategy implements UrlHandlingStrategy {
  shouldProcessUrl(url: any): boolean {
    // When it returns true, the router will execute the regular navigation.
    // When it returns false, the router will set the router state to an empty state.
    // As a result, all the active components will be destroyed.

    // Return true for routes that the Angular router should handle and false if angularJS should handle
    // TODO: If we need this - then refactor to check against routes table above
    const urlString = url.toString();
    for (const route of routesForAngularJs) {
      if (urlString.startsWith(`/${route.path}`)) {
        // TODO: Perhaps not use startsWith if we have multiple sub paths?
        console.log(
          "HybridUrlHandlingStrategy: Angular letting angularJS handle router:",
          urlString,
          url
        );
        // HACK: disable forwarding since it does not work - so we use placeholder components in angular
        // return false;
        // Return true will let the Angular router skip resolve
      }
    }

    return true;
  }

  extract(url: any): any {
    return url;
  }

  merge(url: any, whole: any): any {
    return url;
  }
}

/**
 * In order to let Angular handle routing it was not possible to keep using #/ in the URL
 * so we've moved to Angular's default PathLocationStrategy. To ensure that old links work (especially in practice
 * management systems like MacPractice) the #/ must be removed seamlessly.
 */

@NgModule({
  // NOTE: There are no declarations. Declarations are the responsibility of the companion module.

  imports: [
    RouterModule.forRoot(APP_ROUTES, {
      useHash: false,
      initialNavigation: true, // if false then router will not have correct route before user selects route
      onSameUrlNavigation: "reload",
      enableTracing: true,
    }),

    LocationUpgradeModule.config({
      useHash: false,
      hashPrefix: "",
    }),
  ],

  exports: [RouterModule],
  // We can't use a urlHandlingStrategy because we need both frameworks to clear their current router outlet /
  // new-view when changing route
  providers: [
    { provide: UrlHandlingStrategy, useClass: HybridUrlHandlingStrategy },
  ],
})
export class AppRoutingModule {}

// TODO: Do we need these?
export const routingComponents = [
  Ng1PlaceholderComponent,
  AngularJSComponent,
  // Ng1AppointmentsWapperDirective,
  // AppointmentsComponent
  // PatientsComponent
  // AdminUserDevicesComponent
];
